import React, { useState, useEffect, useRef } from "react";
import { Settings } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { StatusGroup } from "./StatusGroup";
import { EmptyState } from "./EmptyState";
import { STATUS_CONSTANTS, IconType } from "./constants";

export interface StatusInterface {
  id: string;
  name: string;
  count: number;
  color: {
    value: string;
  };
  isDefault?: boolean;
}

export interface StatusGroupType {
  type: string;
  name: string;
  count: number;
  statuses: StatusInterface[];
}

import { ColumnConfig } from "./utils/columnConfigUtils";

interface StatusesContentProps {
  statusGroups: StatusGroupType[];
  getStatusIcon: (status: string, iconType?: IconType) => React.ReactNode;
  onCreateStatus: (statusName: string, statusType: string) => void;
  onUpdateStatus: (statusId: string, newName: string) => Promise<void>;
  onDeleteStatus: (statusId: string) => void;
  columnConfig?: ColumnConfig[];
}

export const StatusesContent: React.FC<StatusesContentProps> = React.memo(
  ({
    statusGroups,
    getStatusIcon,
    onCreateStatus,
    onUpdateStatus,
    onDeleteStatus,
    columnConfig = [],
  }) => {
    const [newlyCreatedStatusId, setNewlyCreatedStatusId] = useState<
      string | null
    >(null);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleCreateStatus = (statusName: string, statusType: string) => {
      setNewlyCreatedStatusId(uuidv4());
      onCreateStatus(statusName, statusType);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(
        () => setNewlyCreatedStatusId(null),
        STATUS_CONSTANTS.HIGHLIGHT_DURATION,
      );
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="max-w-[640px] mx-auto my-auto pb-4">
        <div className="flex flex-col justify-between py-2.5 space-y-1 mt-10">
          <h2 className="text-lg font-medium text-secondary-n-50">
            Project Statuses
          </h2>
          <p className="text-sm text-secondary-n-400">
            Configure the stages your projects follow from start to completion
          </p>
        </div>
        <div className="flex flex-col p-2 border border-border-primary rounded-md">
          <div className="flex-1 scrollbar-hide min-h-0">
            {statusGroups.length > 0 ? (
              statusGroups.map((group) => (
                <StatusGroup
                  key={group.type}
                  group={group}
                  getStatusIcon={getStatusIcon}
                  onCreateStatus={handleCreateStatus}
                  onUpdateStatus={onUpdateStatus}
                  onDeleteStatus={onDeleteStatus}
                  columnConfig={columnConfig}
                  newlyCreatedStatusId={newlyCreatedStatusId}
                />
              ))
            ) : (
              <EmptyState
                icon={Settings}
                title="No statuses found"
                actionLabel="Status"
                onAction={() => handleCreateStatus("New Status", "backlog")}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

StatusesContent.displayName = "StatusesContent";
